/**
 * useStudentShellState — les gestes de la coquille de l'élève : la feuille « moi » ouverte ou non,
 * la bascule vers un frère ou une sœur, l'entrée dans l'espace parent, « Mon compte », « Se
 * déconnecter ».
 *
 * Les gestes sont ceux de l'en-tête legacy (`StudentMainMenuHeader.js`) : basculer, c'est
 * `switchAccount` avec le compte gardé sur l'appareil puis relire le compte (`getUser`), la bascule
 * restant « en cours » jusqu'à l'issue de cette lecture — plus une relecture de `static-data`, que
 * le legacy n'a pas : l'interrupteur de l'espace se recalcule pour le nouveau compte ; « Espace parent », c'est poser la famille
 * puis le mode parent ; « Mon compte » ouvre `account`. La déconnexion est le port de `useLogout`
 * (`useLogoutAdapter`).
 */
import { useNavigation } from "@react-navigation/native"
import { useCallback, useEffect, useReducer, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"

import { apiRequest } from "../../../../../data/redux/actions/api"
import { switchAccount } from "../../../../../data/redux/actions/authTokens"
import { setIsParentMode, setParentModeFamilyId } from "../../../../../data/redux/actions/parentMode"
import { authService } from "../../../../../data/services/authService"
import { staticDataService } from "../../../../../data/services/staticDataService"
import { selectSessionStoredTokens } from "../../../../../data/utility/familyUtils"
import { type UIParentChild } from "../../types"
import { useLogoutAdapter } from "../../hooks/useLogoutAdapter"
import { type StudentShellViewModel } from "../../../view-models/student/StudentShell/studentShellViewModel"
import { initialStudentShellUiState, studentShellUiReducer } from "./studentShellUiState"

type StoredAccount = { userId?: string | number; token?: string }
type PushNavigation = { push: (route: string, params?: object) => void }

export function useStudentShellState(vm: Pick<StudentShellViewModel, "userId" | "familyId">) {
    const { userId, familyId } = vm
    const dispatch = useDispatch()
    const navigation = useNavigation() as unknown as PushNavigation
    const [ui, dispatchUi] = useReducer(studentShellUiReducer, initialStudentShellUiState)
    const { logout, loggingOut } = useLogoutAdapter()

    const storedTokens = useSelector(selectSessionStoredTokens) as StoredAccount[] | undefined
    // Lu pendant une bascule seulement : sinon chaque relecture du compte redessinerait l'en-tête.
    const getUserRequest = useSelector(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (state: any) => ui.switchingId != null && !!state?.api?.getUser?.request
    )

    // La bascule se termine quand la relecture du compte qu'elle a lancée se termine, réussie ou non.
    const sawRequestRef = useRef(false)
    useEffect(() => {
        if (ui.switchingId == null) {
            sawRequestRef.current = false
            return
        }
        if (getUserRequest) {
            sawRequestRef.current = true
            return
        }
        if (sawRequestRef.current) dispatchUi({ type: "END_SWITCH" })
    }, [getUserRequest, ui.switchingId])

    const selectProfile = useCallback(
        (profile: UIParentChild) => {
            if (profile.id === userId) return
            const account = storedTokens?.find((candidate) => candidate?.userId === profile.id)
            if (!account?.token) return
            dispatchUi({ type: "START_SWITCH", id: profile.id })
            dispatch(switchAccount(account) as never)
            dispatch(apiRequest(authService.getUser) as never)
            // `static-data` se relit avec le jeton du compte choisi, comme à l'ouverture de l'app.
            dispatch(apiRequest(staticDataService.getStaticData) as never)
        },
        [dispatch, storedTokens, userId]
    )

    const enterParentSpace = useCallback(() => {
        if (familyId == null) return
        dispatch(setParentModeFamilyId(familyId) as never)
        dispatch(setIsParentMode(true) as never)
    }, [dispatch, familyId])

    return {
        sheetOpen: ui.sheetOpen,
        switchingId: ui.switchingId,
        openSheet: () => dispatchUi({ type: "OPEN_SHEET" }),
        closeSheet: () => dispatchUi({ type: "CLOSE_SHEET" }),
        selectProfile,
        enterParentSpace: familyId == null ? undefined : enterParentSpace,
        openAccount: () => navigation.push("account"),
        logout,
        loggingOut,
    }
}
